import { HAND_CATEGORY, rankOf, suitOf, RANK_CHARS, type Card } from "@joker-holdem/core";

// SUIT_CHARS order in core is "cdhs" (club, diamond, heart, spade).
// docs/UI_REFERENCE.md 4-color deck: spade=dark-gray, heart=red, diamond=blue, club=green.
const SUIT_SYMBOLS = ["♣", "♦", "♥", "♠"] as const;
const SUIT_CLASSES = ["suit-club", "suit-diamond", "suit-heart", "suit-spade"] as const;

export function rankChar(card: Card): string {
  return RANK_CHARS[rankOf(card)] ?? "?";
}

export function suitSymbol(card: Card): string {
  return SUIT_SYMBOLS[suitOf(card)] ?? "?";
}

export function suitClass(card: Card): string {
  return SUIT_CLASSES[suitOf(card)] ?? "";
}

const CATEGORY_NAMES_JA = [
  "ハイカード",
  "ワンペア",
  "ツーペア",
  "スリーカード",
  "ストレート",
  "フラッシュ",
  "フルハウス",
  "フォーカード",
  "ストレートフラッシュ",
];

export function categoryNameJa(category: number): string {
  return CATEGORY_NAMES_JA[category] ?? `役(${category})`;
}

/**
 * `core`'s HandCategory doesn't distinguish royal flush from any other
 * straight flush (GAME_RULES.md has no notion of it) -- it's purely a web
 * presentation detail (result badge coloring, フィードバック #1 2026-07-17),
 * so the check lives here instead of in `@joker-holdem/core`.
 *
 * A royal flush is an ace-high straight flush: a straight flush whose best
 * five cards include both an ace (rank 12) and a king (rank 11). This
 * deliberately excludes the wheel straight flush (A-2-3-4-5), which
 * contains an ace but not a king.
 */
export function isRoyalFlush(category: number, bestFive: readonly Card[]): boolean {
  if (category !== HAND_CATEGORY.STRAIGHT_FLUSH) return false;
  let hasAce = false;
  let hasKing = false;
  for (const card of bestFive) {
    const r = rankOf(card);
    if (r === 12) hasAce = true;
    else if (r === 11) hasKing = true;
  }
  return hasAce && hasKing;
}

/** Result-badge category key: like `categoryNameJa`'s index, but splits royal flush out of straight flush for CSS/label purposes. */
export type BadgeCategoryKey =
  | "high-card"
  | "one-pair"
  | "two-pair"
  | "three-of-a-kind"
  | "straight"
  | "flush"
  | "full-house"
  | "four-of-a-kind"
  | "straight-flush"
  | "royal-flush";

const BADGE_KEYS: readonly BadgeCategoryKey[] = [
  "high-card",
  "one-pair",
  "two-pair",
  "three-of-a-kind",
  "straight",
  "flush",
  "full-house",
  "four-of-a-kind",
  "straight-flush",
];

const BADGE_LABELS_JA: Record<BadgeCategoryKey, string> = {
  "high-card": "ハイカード",
  "one-pair": "ワンペア",
  "two-pair": "ツーペア",
  "three-of-a-kind": "スリーカード",
  straight: "ストレート",
  flush: "フラッシュ",
  "full-house": "フルハウス",
  "four-of-a-kind": "フォーカード",
  "straight-flush": "ストレートフラッシュ",
  "royal-flush": "ロイヤルフラッシュ",
};

/** Result-badge category key for a showdown hand (folds royal flush out of straight-flush). */
export function badgeCategoryKey(category: number, bestFive: readonly Card[]): BadgeCategoryKey {
  if (isRoyalFlush(category, bestFive)) return "royal-flush";
  return BADGE_KEYS[category] ?? "high-card";
}

/** Japanese label for a result badge's category key. */
export function badgeCategoryLabelJa(key: BadgeCategoryKey): string {
  return BADGE_LABELS_JA[key];
}
